import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  UserIcon,
  ShieldCheckIcon,
  CogIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'badge-warning', icon: ClockIcon },
  in_progress: { label: 'In Progress', color: 'badge-info', icon: ChatBubbleLeftIcon },
  awaiting_user: { label: 'Awaiting Your Reply', color: 'badge-warning', icon: ExclamationTriangleIcon },
  resolved: { label: 'Resolved', color: 'badge-success', icon: CheckCircleIcon },
  closed: { label: 'Closed', color: 'badge-neutral', icon: CheckCircleIcon }
};

const PRIORITY_COLORS = {
  low: 'text-surface-500',
  medium: 'text-blue-500',
  high: 'text-amber-500',
  urgent: 'text-red-500'
};

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  
  useEffect(() => {
    loadTicket();
  }, [id]);
  
  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const loadTicket = async () => {
    try {
      setLoading(true);
      const response = await apiHelpers.getTicket(id);
      setTicket(response.data.ticket);
      setMessages(response.data.messages);
    } catch (error) {
      toast.error('Failed to load ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const response = await apiHelpers.addTicketMessage(id, newMessage);
      setMessages(prev => [...prev, response.data.message]);
      setNewMessage('');
      // Reload ticket to get updated status
      const ticketResponse = await apiHelpers.getTicket(id);
      setTicket(ticketResponse.data.ticket);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  const handleCloseTicket = async () => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;
    
    setClosing(true);
    try {
      await apiHelpers.closeTicket(id);
      toast.success('Ticket closed');
      loadTicket();
    } catch (error) {
      toast.error('Failed to close ticket');
    } finally {
      setClosing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-500">Ticket not found</p>
        <button onClick={() => navigate('/tickets')} className="btn-primary mt-4">
          Back to Tickets
        </button>
      </div>
    );
  }
  
  const StatusIcon = STATUS_CONFIG[ticket.status]?.icon || ClockIcon;
  const isClosed = ['closed', 'resolved'].includes(ticket.status);
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/tickets')}
            className="p-2 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg mt-1"
          >
            <ArrowLeftIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
          </button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-xl font-bold text-surface-900 dark:text-white">
                {ticket.subject}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={STATUS_CONFIG[ticket.status]?.color}>
                <StatusIcon className="w-3.5 h-3.5 inline mr-1" />
                {STATUS_CONFIG[ticket.status]?.label}
              </span>
              <span className="badge-neutral capitalize">
                {ticket.category?.replace('_', ' ')}
              </span>
              <span className={`text-sm ${PRIORITY_COLORS[ticket.priority]}`}>
                {ticket.priority} priority
              </span>
              <span className="text-sm text-surface-500">
                #{ticket._id.slice(-6).toUpperCase()}
              </span>
            </div>
          </div>
        </div>
        
        {!isClosed && (
          <button
            onClick={handleCloseTicket}
            disabled={closing}
            className="btn-ghost text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
          >
            <XMarkIcon className="w-4 h-4" />
            Close Ticket
          </button>
        )}
      </div>
      
      {/* Technical Details (if present) */}
      {ticket.errorDetails?.errorMessage && (
        <div className="card bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
          <h3 className="font-medium text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5" />
            Error Details
          </h3>
          <pre className="text-sm text-amber-800 dark:text-amber-300 font-mono whitespace-pre-wrap bg-amber-100 dark:bg-amber-900/40 p-3 rounded-lg">
            {ticket.errorDetails.errorMessage}
          </pre>
          {ticket.errorDetails.stepsToReproduce && (
            <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800">
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-1">Steps to Reproduce:</p>
              <p className="text-sm text-amber-800 dark:text-amber-300 whitespace-pre-wrap">
                {ticket.errorDetails.stepsToReproduce}
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Messages */}
      <div className="card">
        <h3 className="font-semibold text-surface-900 dark:text-white mb-4">
          Conversation
        </h3>
        
        <div className="space-y-4 max-h-[500px] overflow-y-auto mb-4">
          {messages.map((message, index) => {
            const isUser = message.senderType === 'user';
            const isSystem = message.senderType === 'system';
            
            if (isSystem) {
              return (
                <div key={message._id || index} className="flex justify-center">
                  <div className="flex items-center gap-2 text-xs text-surface-500 bg-surface-100 dark:bg-surface-800 px-3 py-1.5 rounded-full">
                    <CogIcon className="w-3.5 h-3.5" />
                    {message.content}
                    <span className="text-surface-400">
                      {formatDistanceToNow(new Date(message.createdAt))} ago
                    </span>
                  </div>
                </div>
              );
            }
            
            return (
              <div
                key={message._id || index}
                className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${isUser ? 'order-2' : ''}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {!isUser && (
                      <div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <ShieldCheckIcon className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                      </div>
                    )}
                    <span className="text-xs font-medium text-surface-600 dark:text-surface-400">
                      {isUser 
                        ? 'You' 
                        : `${message.senderId?.firstName || 'Support'}`
                      }
                    </span>
                    {message.isAIGenerated && (
                      <SparklesIcon className="w-3.5 h-3.5 text-purple-500" title="AI-assisted response" />
                    )}
                    <span className="text-xs text-surface-400">
                      {format(new Date(message.createdAt), 'MMM d, h:mm a')}
                    </span>
                  </div>
                  
                  <div
                    className={`p-3 rounded-lg ${
                      isUser
                        ? 'bg-primary-600 text-white'
                        : 'bg-surface-100 dark:bg-surface-800 text-surface-800 dark:text-surface-200'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Reply Input */}
        {!isClosed ? (
          <form onSubmit={handleSendMessage} className="border-t border-surface-200 dark:border-surface-700 pt-4">
            <div className="flex gap-3">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="input flex-1 resize-none h-20"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handleSendMessage(e);
                  }
                }}
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="btn-primary self-end"
              >
                {sending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <PaperAirplaneIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-surface-500 mt-2">
              Press Cmd+Enter or Ctrl+Enter to send
            </p>
          </form>
        ) : (
          <div className="border-t border-surface-200 dark:border-surface-700 pt-4">
            <p className="text-center text-surface-500">
              This ticket is closed. 
              <button
                onClick={() => navigate('/help')}
                className="text-primary-600 hover:underline ml-1"
              >
                Open a new ticket
              </button>
              {' '}if you need further assistance.
            </p>
          </div>
        )}
      </div>
      
      {/* Ticket Info */}
      <div className="card">
        <h3 className="font-semibold text-surface-900 dark:text-white mb-4">
          Ticket Information
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-surface-500">Created</p>
            <p className="text-surface-900 dark:text-white">
              {format(new Date(ticket.createdAt), 'PPP p')}
            </p>
          </div>
          <div>
            <p className="text-surface-500">Last Updated</p>
            <p className="text-surface-900 dark:text-white">
              {formatDistanceToNow(new Date(ticket.lastMessageAt || ticket.updatedAt))} ago
            </p>
          </div>
          {ticket.closedAt && (
            <div>
              <p className="text-surface-500">Closed</p>
              <p className="text-surface-900 dark:text-white">
                {format(new Date(ticket.closedAt), 'PPP p')}
              </p>
            </div>
          )}
          {ticket.assignedTo && (
            <div>
              <p className="text-surface-500">Assigned To</p>
              <p className="text-surface-900 dark:text-white">
                {ticket.assignedTo.firstName} {ticket.assignedTo.lastName}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
